import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import ComplianceOverviewCard from '../components/compliance/ComplianceOverviewCard';
import ComplianceCheckCard from '../components/compliance/ComplianceCheckCard';
import Revvy from '../components/Revvy';
import SiteOverviewModal from '../components/SiteOverviewModal';
import usePageMetadata from '../utils/usePageMetadata';
import './Compliance.css';

const STATUS_WEIGHT = {
  pass: 1,
  warning: 0.5,
  fail: 0,
};

const scoreChecks = (checks) => {
  const totalChecks = checks.length;
  if (totalChecks === 0) {
    return {
      score: 0,
      statusCounts: { pass: 0, warning: 0, fail: 0 },
      overallStatus: 'fail',
    };
  }

  const statusCounts = checks.reduce(
    (accumulator, check) => {
      accumulator[check.status] += 1;
      return accumulator;
    },
    { pass: 0, warning: 0, fail: 0 }
  );

  const rawScore = checks.reduce((total, check) => {
    return total + STATUS_WEIGHT[check.status] * (100 / totalChecks);
  }, 0);

  const score = Math.round(rawScore);
  const overallStatus =
    statusCounts.fail > 0 ? 'fail' : statusCounts.warning > 0 ? 'warning' : 'pass';

  return { score, statusCounts, overallStatus };
};

const AdSenseApproval = () => {
  const { token } = useAuth();
  const [url, setUrl] = useState('');
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedAt, setGeneratedAt] = useState(null);
  const [scanStatus, setScanStatus] = useState('idle');
  const [reportScore, setReportScore] = useState(null);
  const [reportStatusCounts, setReportStatusCounts] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const showRevvy = true;
  const [revvyStateId, setRevvyStateId] = useState(null);
  const [showSiteOverview, setShowSiteOverview] = useState(false);

  // Show Revvy immediately, and update state based on scan status
  useEffect(() => {
    if (scanStatus === 'completed' && checks.length > 0) {
      // After scan completes, show post-scan state
      setRevvyStateId('prerev_adsense_post_scan');
    } else if (scanStatus === 'idle' || scanStatus === 'error') {
      // Show intro state when no scan has been run yet - asks about AdSense approval
      setRevvyStateId('pre_signup_intro');
    }
  }, [scanStatus, checks.length]);

  usePageMetadata({
    title: 'Free AdSense Approval Scanner | Get Your Site Ready for Google AdSense',
    description:
      'Free AdSense approval scanner powered by Revvy. Check if your website meets Google AdSense requirements before applying. Get instant feedback on compliance issues.',
    canonicalUrl: 'https://theadshop.co/growth/adsense',
    meta: [
      { property: 'og:title', content: 'Free AdSense Approval Scanner | Get Your Site Ready for Google AdSense' },
      {
        property: 'og:description',
        content:
          'Free AdSense approval scanner powered by Revvy. Check if your website meets Google AdSense requirements before applying.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/growth/adsense' },
      { property: 'og:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: 'https://theadshop.co/images/dashboard-preview.webp' },
      { name: 'twitter:title', content: 'Free AdSense Approval Scanner | Get Your Site Ready for Google AdSense' },
      {
        name: 'twitter:description',
        content:
          'Free AdSense approval scanner powered by Revvy. Check if your website meets Google AdSense requirements before applying.',
      },
    ],
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'AdSense Approval Scanner',
        description: 'Free AdSense approval scanner powered by Revvy. Check if your website meets Google AdSense requirements.',
        url: 'https://theadshop.co/growth/adsense',
        applicationCategory: 'WebApplication',
        operatingSystem: 'Any',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        featureList: [
          'AdSense compliance checking',
          'ads.txt validation',
          'HTTPS & SSL validation',
          'Privacy policy detection',
          'Content quality assessment',
          'Site structure analysis',
        ],
      },
    ],
  });

  const handleScan = async () => {
    if (!url.trim()) {
      setError('Please enter a URL to scan');
      return;
    }

    setLoading(true);
    setError('');
    setScanStatus('running');
    setStartedAt(new Date().toISOString());
    setChecks([]);
    setReportScore(null);
    setReportStatusCounts(null);

    try {
      // Normalize URL
      let normalizedUrl = url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      // Use the compliance API endpoint - it checks AdSense requirements
      const response = await axios.post('/api/compliance/public', {
        url: normalizedUrl,
      });

      const data = response.data || {};
      if (Array.isArray(data.checks) && data.checks.length > 0) {
        setChecks(data.checks);
      }
      if (data.generatedAt) {
        setGeneratedAt(data.generatedAt);
      }
      setScanStatus(data.status || 'completed');
      setReportScore(typeof data.score === 'number' ? data.score : null);
      setReportStatusCounts(
        data.statusCounts && typeof data.statusCounts === 'object' ? data.statusCounts : null
      );
      setStartedAt(data.startedAt || null);

      if (data.status === 'error') {
        setError(data.errorMessage || 'AdSense approval scan failed. Please try again.');
      }
    } catch (err) {
      console.error('Error running AdSense approval scan:', err);
      setError(
        err.response?.data?.message || 'Failed to run AdSense approval scan. Please check the URL and try again.'
      );
      setScanStatus('error');
      setChecks([]);
      setGeneratedAt(null);
      setReportScore(null);
      setReportStatusCounts(null);
      setStartedAt(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleScan();
    }
  };

  const fallbackMetrics = useMemo(() => scoreChecks(checks), [checks]);

  const fallbackStatusCounts = fallbackMetrics.statusCounts;
  const effectiveScore = reportScore ?? fallbackMetrics.score;
  const effectiveStatusCounts = useMemo(
    () => ({
      pass: reportStatusCounts?.pass ?? fallbackStatusCounts.pass,
      warning: reportStatusCounts?.warning ?? fallbackStatusCounts.warning,
      fail: reportStatusCounts?.fail ?? fallbackStatusCounts.fail,
    }),
    [reportStatusCounts, fallbackStatusCounts]
  );
  const effectiveOverallStatus =
    effectiveStatusCounts.fail > 0
      ? 'fail'
      : effectiveStatusCounts.warning > 0
      ? 'warning'
      : 'pass';

  const noReportData = !checks.length && scanStatus !== 'running';
  const issuesNeedingAttention =
    (effectiveStatusCounts.fail || 0) + (effectiveStatusCounts.warning || 0);
  let highlight;
  if (scanStatus === 'running') {
    highlight = "AdSense approval scan in progress. We'll update results as each check finishes.";
  } else if (scanStatus === 'error') {
    highlight = 'We could not complete the scan. Try running it again.';
  } else if (noReportData) {
    highlight = 'Enter your website URL above to check if it meets Google AdSense requirements.';
  } else if (issuesNeedingAttention === 0) {
    highlight = 'Great! Your site appears ready for AdSense approval. Apply to Google AdSense when ready.';
  } else {
    highlight = `Your site is ${effectiveScore}% ready for AdSense. ${issuesNeedingAttention} issue${
      issuesNeedingAttention > 1 ? 's' : ''
    } need attention before applying.`;
  }

  const isReadyForAdSense = effectiveScore >= 80 && effectiveStatusCounts.fail === 0;

  return (
    <div className="compliance-page">
      <main className="compliance-content">
        {/* Revvy Introduction - Always visible */}
        {showRevvy && (
          <Revvy 
            initialStateId={revvyStateId || 'pre_signup_intro'}
            onStateChange={(newState) => {
              setRevvyStateId(newState.id);
            }}
            scanContext={{
              hasScanResults: checks.length > 0,
              score: effectiveScore,
              statusCounts: effectiveStatusCounts,
              totalChecks: checks.length,
              isReady: isReadyForAdSense,
              checks: checks
            }}
          />
        )}

        <div className="compliance-header">
          <div className="header-copy">
            <h1>AdSense Approval Scanner</h1>
            <p style={{ fontSize: '0.95rem', color: '#666', lineHeight: '1.6' }}>
              Enter your website URL below and I'll scan it for AdSense compliance. I'll check everything Google looks for when reviewing AdSense applications, including HTTPS configuration, required pages (privacy policy, contact, about), content quality, site structure, and technical requirements like ads.txt and robots.txt. The scan takes just a few moments and provides actionable feedback on what needs attention before you apply. Once you're approved, create a free account and I'll help you optimize your AdSense revenue with data-driven insights and recommendations.
            </p>
          </div>
          {url && (
            <div style={{ marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => setShowSiteOverview(true)}
                className="site-overview-btn"
                style={{
                  background: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f3f4f6';
                }}
              >
                📊 View Site Overview
              </button>
            </div>
          )}
          <div className="site-selector">
            <label htmlFor="url-input">Website URL</label>
            <div className="site-selector-controls">
              <input
                id="url-input"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="https://example.com"
                disabled={loading}
                className="url-input"
              />
              <button
                type="button"
                className="refresh-report-btn"
                onClick={handleScan}
                disabled={loading || !url.trim()}
              >
                {loading ? 'Scanning...' : 'Scan for AdSense'}
              </button>
            </div>
            {generatedAt && (
              <p className="generated-at">Report generated {new Date(generatedAt).toLocaleString()}</p>
            )}
            {scanStatus === 'running' && (
              <div className="scan-progress">
                <div className="scan-progress-header">
                  <span className="scan-progress-indicator" aria-hidden="true" />
                  <span>AdSense approval scan in progress</span>
                </div>
                {startedAt && (
                  <p className="scan-progress-started">
                    Started {new Date(startedAt).toLocaleString()}
                  </p>
                )}
                <p className="scan-progress-message">Checking AdSense requirements...</p>
              </div>
            )}
            {scanStatus === 'error' && (
              <div className="scan-progress scan-progress-error">
                <div className="scan-progress-header">
                  <span className="scan-progress-indicator error" aria-hidden="true" />
                  <span>AdSense approval scan failed</span>
                </div>
                <p className="scan-progress-message">
                  {error || 'We could not finish the scan. Try again soon.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {error && scanStatus !== 'error' && <div className="compliance-error">{error}</div>}

        {checks.length > 0 && (
          <>
            {/* Show scan results summary - Revvy will explain these */}
            <ComplianceOverviewCard
              score={effectiveScore}
              totalChecks={checks.length}
              statusCounts={effectiveStatusCounts}
              highlight={highlight}
              breakdown={{ overallStatus: effectiveOverallStatus }}
            />

            {/* Detailed check results */}
            <section className="compliance-cards">
              {checks.map((check) => (
                <div key={check.id} id={`check-${check.id}`}>
                  <ComplianceCheckCard check={check} />
                </div>
              ))}
            </section>
          </>
        )}

        {noReportData && scanStatus !== 'running' && (
          <div className="compliance-results-empty">
            <div className="compliance-features-list">
              <h4>What I check for AdSense approval:</h4>
              <ul>
                <li><strong>HTTPS & SSL Validation</strong> - AdSense requires secure HTTPS connections</li>
                <li><strong>Privacy Policy</strong> - Required for AdSense approval</li>
                <li><strong>ads.txt File</strong> - Ensures proper ad network authorization</li>
                <li><strong>Content Quality</strong> - Checks for sufficient content and structure</li>
                <li><strong>Site Accessibility</strong> - Verifies robots.txt and site structure</li>
                <li><strong>Ad Policy Compliance</strong> - Reviews compliance with AdSense policies</li>
              </ul>
            </div>
            {!token && (
              <div style={{ marginTop: '2rem', padding: '1rem', background: '#f0f0f0', borderRadius: '8px' }}>
                <p style={{ margin: 0 }}>
                  <strong>Already approved?</strong> <Link to="/signup">Create a free account</Link> and I'll help you optimize your AdSense revenue.
                </p>
              </div>
            )}
          </div>
        )}

        <SiteOverviewModal
          isOpen={showSiteOverview}
          onClose={() => setShowSiteOverview(false)}
          url={url}
        />
      </main>
    </div>
  );
};

export default AdSenseApproval;

